import BaseEntity from '../../base/BaseEntity';
import User from '../../user/User';
import { SpaceMemberRole } from './SpaceMemberRole';
import Filter from '../../base/filter/Filter';
import SortFilter from '../../base/filter/SortFilter';
import InputFilter from '../../base/filter/InputFilter';
import SafeUtil from '../../../util/SafeUtil';
import Pager from '../../base/Pager';

export interface SpaceMemberFormValues {
  spaceUuid: string;
  userUuid: string;
  role: SpaceMemberRole;
  uuid: string | null;
}

export interface DeptBatchOperationRequest {
  spaceUuid: string;
  deptIds: string[];
}

export interface DeptBatchOperationResponse {
  successCount: number;  // 成功数量
  errorCount: number;    // 失败数量
  deptCount: number;     // 部门数量
  totalCount: number;    // 总处理数量（成功+失败）
  errors: string[];      // 错误信息（字符串数组）
  message: string;       // 消息提示
}

export interface DeptSyncOperationRequest {
  spaceUuid: string;
  deptIds: string[];
  removeLeftUsers?: boolean; // 是否移除已离开部门的用户
}

export interface DeptSyncOperationResponse {
  addedCount: number;
  removedCount: number;
  skippedCount: number;
  details?: {
    added: Array<{
      userId: string;
      username: string;
    }>;
    removed: Array<{
      userId: string;
      username: string;
    }>;
  };
}

export default class SpaceMember extends BaseEntity {
  spaceUuid: string | null = null;
  userUuid: string | null = null;
  role: SpaceMemberRole | null = null;
  user: User | null = null;
  
  // 新增：部门相关字段
  fromDeptId?: string; // 如果成员来自部门绑定，记录部门ID
  deptName?: string; // 部门名称（用于显示）
  isDeptMember: boolean = false; // 是否来自部门绑定

  // API URL 常量
  static URL_SPACE_MEMBER_MINE = '/api/space/member/mine';
  static URL_BATCH_ADD_BY_DEPT = '/api/space/member/batchAddByDept';
  static URL_SYNC_DEPT_MEMBERS = '/api/space/member/syncDeptMembers';
  static URL_BATCH_REMOVE_BY_DEPT = '/api/space/member/batchRemoveByDept';
  static URL_GET_DEPT_MEMBERS = '/api/space/member/deptMembers';

  constructor(reactComponent?: React.Component) {
    super(reactComponent);
  }

  getTAG(): string {
    return 'spaceMember';
  }

  assign(obj: any) {
    super.assign(obj);
    this.assignEntity('user', User);
    
    // 处理部门相关字段
    if (obj.fromDeptId !== undefined) {
      this.fromDeptId = obj.fromDeptId;
    }
    
    if (obj.deptName !== undefined) {
      this.deptName = obj.deptName;
    }
    
    if (obj.isDeptMember !== undefined) {
      this.isDeptMember = obj.isDeptMember;
    } else if (obj.fromDeptId !== undefined && obj.fromDeptId !== null) {
      // 如果有fromDeptId，则自动标记为部门成员
      this.isDeptMember = true;
    }
  }

  getFilters(): Filter[] {
    return [
      ...super.getFilters(),
      new InputFilter('空间ID', 'spaceUuid', '按空间ID搜索', false),
      // 新增：按部门筛选
      new InputFilter('部门ID', 'deptId', '按部门ID筛选', false),
      // 新增：按是否来自部门绑定筛选
      new InputFilter('部门成员', 'isDeptMember', '筛选部门绑定成员', false),
    ];
  }

  getForm(): SpaceMemberFormValues {
    return {
      spaceUuid: this.spaceUuid!,
      userUuid: this.userUuid!,
      role: this.role!,
      uuid: this.uuid ? this.uuid : null,
    };
  }

  // 检查是否来自部门绑定
  isFromDept(): boolean {
    return this.isDeptMember || !!this.fromDeptId;
  }

  // 获取部门来源信息
  getDeptSourceInfo(): string {
    if (this.deptName) {
      return `来自部门: ${this.deptName}`;
    } else if (this.fromDeptId) {
      return `来自部门ID: ${this.fromDeptId}`;
    } else if (this.isDeptMember) {
      return '部门绑定成员';
    }
    return '手动添加';
  }

  httpMine(spaceUuid: string, successCallback?: any, errorCallback?: any) {
    this.httpGet(
      SpaceMember.URL_SPACE_MEMBER_MINE,
      { spaceUuid },
      (response: any) => {
        this.assign(response.data.data);
        SafeUtil.safeCallback(successCallback)(response);
      },
      errorCallback
    );
  }


// 修改后的 httpBatchAddByDept 方法，支持角色参数
httpBatchAddByDept(
  spaceUuid: string, 
  deptIds: string[], 
  role?: SpaceMemberRole, // 新增：可选的角色参数
  successCallback?: Function, 
  errorCallback?: Function
) {
  console.log('=== httpBatchAddByDept 开始执行 ===');
  console.log('空间UUID:', spaceUuid);
  console.log('部门IDs:', deptIds);
  console.log('角色:', role);
  
  const data: any = {
    spaceUuid,
    deptIds
  };
  
  // 如果有角色参数，添加到请求数据中
  if (role !== undefined) {
    data.role = role;
  }
  
  console.log('请求数据:', data);
  
  this.httpPost('/api/space/member/batchAddByDept', data, 
    (response: any) => {
      console.log('=== 成功回调 ===');
      console.log('完整响应:', response);
      
      // 检查响应结构
      if (response && typeof response === 'object') {
        console.log('响应对象结构:', Object.keys(response));
        
        // 后台直接返回业务数据，没有包装在 data 字段中
        // 所以 response 本身就是业务数据
        
        // 但是需要检查是否有错误信息
        if (response.errorCount !== undefined || response.successCount !== undefined) {
          // 这是业务数据
          console.log('直接返回业务数据给 successCallback');
          if (successCallback) {
            successCallback(response);
          }
          return;
        }
        
        // 或者响应可能包装在 data 字段中
        if (response.data && (response.data.errorCount !== undefined || response.data.successCount !== undefined)) {
          console.log('响应数据在 data 字段中');
          if (successCallback) {
            successCallback(response.data);
          }
          return;
        }
      }
      
      // 如果上述情况都不符合，可能有其他结构
      console.log('未知响应结构，传递整个响应');
      if (successCallback) {
        successCallback(response);
      }
    },
    (error: any) => {
      console.log('=== 错误回调 ===');
      console.log('错误信息:', error);
      if (errorCallback) {
        errorCallback(error);
      }
    }
  );
}
  // 修改为实例方法：同步部门成员
  httpSyncDeptMembers(
    spaceUuid: string, 
    deptIds: string[], 
    removeLeft: boolean = true,
    successCallback?: (result: DeptSyncOperationResponse) => void, 
    errorCallback?: (error: any) => void
  ) {
    // 将数组转换为逗号分隔的字符串
    const deptIdsStr = deptIds.join(',');
    
    const params = {
      spaceUuid: spaceUuid,
      deptIds: deptIdsStr,
      removeLeft: removeLeft.toString()
    };
    
    // 使用实例方法 this.httpPost
    this.httpPost(SpaceMember.URL_SYNC_DEPT_MEMBERS, params, successCallback, errorCallback);
  }

  // 修改为实例方法：批量移除部门成员
  httpBatchRemoveByDept(
    spaceUuid: string, 
    deptIds: string[], 
    successCallback?: (result: DeptBatchOperationResponse) => void, 
    errorCallback?: (error: any) => void
  ) {
    // 将数组转换为逗号分隔的字符串
    const deptIdsStr = deptIds.join(',');
    
    const params = {
      spaceUuid: spaceUuid,
      deptIds: deptIdsStr
    };
    
    // 使用实例方法 this.httpPost
    this.httpPost(SpaceMember.URL_BATCH_REMOVE_BY_DEPT, params, successCallback, errorCallback);
  }

  // 修改为实例方法：获取部门的用户列表（分页）
  httpGetDeptUsers(
    deptId: string,
    page: number = 0,
    pageSize: number = 10,
    successCallback?: (pager: Pager<User>) => void,
    errorCallback?: (error: any) => void
  ) {
    const params = {
      deptId,
      page,
      pageSize
    };
    
    // 使用实例方法 this.httpGet
    this.httpGet(
      SpaceMember.URL_GET_DEPT_MEMBERS,
      params,
      (response: any) => {
        // 创建User的Pager来解析响应
        const userPager = new Pager<User>(null, User, pageSize);
        userPager.assign(response.data);
        SafeUtil.safeCallback(successCallback)(userPager);
      },
      errorCallback
    );
  }

  // 修改为实例方法：检查用户是否在指定部门中
  httpCheckUserInDept(
    userUuid: string,
    deptId: string,
    successCallback?: (inDept: boolean) => void,
    errorCallback?: (error: any) => void
  ) {
    const params = {
      userUuid,
      deptId
    };
    
    // 使用实例方法 this.httpGet
    this.httpGet(
      '/api/space/member/checkUserInDept',
      params,
      (response: any) => {
        const inDept = response.data?.inDept || false;
        SafeUtil.safeCallback(successCallback)(inDept);
      },
      errorCallback
    );
  }

  // 修改为实例方法：获取空间绑定的部门成员统计
  httpGetDeptMemberStats(
    spaceUuid: string,
    successCallback?: (stats: {
      totalMembers: number;
      deptMembers: number;
      manualMembers: number;
      deptCount: number;
    }) => void,
    errorCallback?: (error: any) => void
  ) {
    const params = {
      spaceUuid
    };
    
    // 使用实例方法 this.httpGet
    this.httpGet(
      '/api/space/member/deptStats',
      params,
      (response: any) => {
        const stats = response.data || {
          totalMembers: 0,
          deptMembers: 0,
          manualMembers: 0,
          deptCount: 0
        };
        SafeUtil.safeCallback(successCallback)(stats);
      },
      errorCallback
    );
  }

  // 修改为实例方法：批量更新部门成员角色
  httpBatchUpdateDeptMemberRole(
    spaceUuid: string, 
    deptIds: string[], 
    role: SpaceMemberRole,
    successCallback?: (result: any) => void, 
    errorCallback?: (error: any) => void
  ) {
    // 将数组转换为逗号分隔的字符串
    const deptIdsStr = deptIds.join(',');
    
    const params = {
      spaceUuid: spaceUuid,
      deptIds: deptIdsStr,
      role: role
    };
    
    // 使用实例方法 this.httpPost
    this.httpPost('/api/space/member/batchUpdateDeptMemberRole', params, successCallback, errorCallback);
  }

  // ============ 静态方法（兼容性） ============

// 静态方法：批量添加部门成员（支持角色参数）
static httpBatchAddByDept(
  spaceUuid: string, 
  deptIds: string[], 
  role?: SpaceMemberRole, // 新增：可选的角色参数
  successCallback?: (result: DeptBatchOperationResponse) => void, 
  errorCallback?: (error: any) => void,
  reactComponent?: React.Component
) {
  console.log('=== 静态方法 httpBatchAddByDept 开始执行 ===');
  console.log('空间UUID:', spaceUuid);
  console.log('部门IDs:', deptIds);
  console.log('角色:', role);
  
  const instance = new SpaceMember(reactComponent);
  instance.httpBatchAddByDept(spaceUuid, deptIds, role, successCallback, errorCallback);
}

  // 静态方法：同步部门成员
  static httpSyncDeptMembers(
    spaceUuid: string, 
    deptIds: string[], 
    removeLeft: boolean = true,
    successCallback?: (result: DeptSyncOperationResponse) => void, 
    errorCallback?: (error: any) => void,
    reactComponent?: React.Component
  ) {
    const instance = new SpaceMember(reactComponent);
    instance.httpSyncDeptMembers(spaceUuid, deptIds, removeLeft, successCallback, errorCallback);
  }

  // 静态方法：批量移除部门成员
  static httpBatchRemoveByDept(
    spaceUuid: string, 
    deptIds: string[], 
    successCallback?: (result: DeptBatchOperationResponse) => void, 
    errorCallback?: (error: any) => void,
    reactComponent?: React.Component
  ) {
    const instance = new SpaceMember(reactComponent);
    instance.httpBatchRemoveByDept(spaceUuid, deptIds, successCallback, errorCallback);
  }

  // 静态方法：获取部门的用户列表
  static httpGetDeptUsers(
    deptId: string,
    page: number = 0,
    pageSize: number = 10,
    successCallback?: (pager: Pager<User>) => void,
    errorCallback?: (error: any) => void,
    reactComponent?: React.Component
  ) {
    const instance = new SpaceMember(reactComponent);
    instance.httpGetDeptUsers(deptId, page, pageSize, successCallback, errorCallback);
  }

  // 静态方法：检查用户是否在指定部门中
  static httpCheckUserInDept(
    userUuid: string,
    deptId: string,
    successCallback?: (inDept: boolean) => void,
    errorCallback?: (error: any) => void,
    reactComponent?: React.Component
  ) {
    const instance = new SpaceMember(reactComponent);
    instance.httpCheckUserInDept(userUuid, deptId, successCallback, errorCallback);
  }

  // 静态方法：获取空间绑定的部门成员统计
  static httpGetDeptMemberStats(
    spaceUuid: string,
    successCallback?: (stats: {
      totalMembers: number;
      deptMembers: number;
      manualMembers: number;
      deptCount: number;
    }) => void,
    errorCallback?: (error: any) => void,
    reactComponent?: React.Component
  ) {
    const instance = new SpaceMember(reactComponent);
    instance.httpGetDeptMemberStats(spaceUuid, successCallback, errorCallback);
  }

  // 静态方法：批量更新部门成员角色
  static httpBatchUpdateDeptMemberRole(
    spaceUuid: string, 
    deptIds: string[], 
    role: SpaceMemberRole,
    successCallback?: (result: any) => void, 
    errorCallback?: (error: any) => void,
    reactComponent?: React.Component
  ) {
    const instance = new SpaceMember(reactComponent);
    instance.httpBatchUpdateDeptMemberRole(spaceUuid, deptIds, role, successCallback, errorCallback);
  }

  // 静态方法：批量更新部门成员角色（不带 http 前缀，兼容旧代码）
  static batchUpdateDeptMemberRole(
    spaceUuid: string, 
    deptIds: string[], 
    role: SpaceMemberRole,
    successCallback?: (result: any) => void, 
    errorCallback?: (error: any) => void,
    reactComponent?: React.Component
  ) {
    const instance = new SpaceMember(reactComponent);
    instance.httpBatchUpdateDeptMemberRole(spaceUuid, deptIds, role, successCallback, errorCallback);
  }

  // 静态方法：获取空间绑定的部门成员统计（不带 http 前缀，兼容旧代码）
  static getDeptMemberStats(
    spaceUuid: string,
    successCallback?: (stats: {
      totalMembers: number;
      deptMembers: number;
      manualMembers: number;
      deptCount: number;
    }) => void,
    errorCallback?: (error: any) => void,
    reactComponent?: React.Component
  ) {
    const instance = new SpaceMember(reactComponent);
    instance.httpGetDeptMemberStats(spaceUuid, successCallback, errorCallback);
  }
}